import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AdminImpersonationLog() {
  const [days, setDays] = useState(7);
  const [adminEmail, setAdminEmail] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ days });
      if (adminEmail) p.set("admin_email", adminEmail);
      if (targetEmail) p.set("target_email", targetEmail);
      const r = await api.get(`/admin/impersonation-log?${p.toString()}`);
      setEvents(r.data.events);
    } catch (e) { toast.error("Failed to load audit log"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="space-y-4" data-testid="admin-impersonation-log-page">
      <header>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Impersonation Log</h1>
        <div className="vx-label mt-1">Read-only audit trail · every impersonated action recorded</div>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {[1, 7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-3 py-1 rounded border text-xs font-mono uppercase tracking-widest ${
                      days === d ? "border-[#dc2626] text-[#dc2626] bg-[#dc2626]/10" : "border-[#1f2937] text-[#9ca3af] hover:bg-[#1f2937]/40"
                    }`}
                    data-testid={`imp-range-${d}d`}>{d}d</button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <Input placeholder="Admin email…" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
                 className="pl-8 bg-[#0a0e1a] border-[#1f2937] font-mono text-xs h-8 w-56" />
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <Input placeholder="Target email…" value={targetEmail} onChange={(e) => setTargetEmail(e.target.value)}
                 className="pl-8 bg-[#0a0e1a] border-[#1f2937] font-mono text-xs h-8 w-56" />
        </div>
        <Button size="sm" variant="outline" onClick={load}
                className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8" data-testid="imp-search-btn">
          Search
        </Button>
      </div>

      <div className="rounded-lg border border-[#1f2937] bg-[#111827] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#0a0e1a] text-[#6b7280]">
            <tr>
              <th className="text-left px-3 py-2">Timestamp (UTC)</th>
              <th className="text-left px-3 py-2">Admin</th>
              <th className="text-left px-3 py-2">Target user</th>
              <th className="text-left px-3 py-2">Endpoint</th>
              <th className="text-left px-3 py-2">Method</th>
            </tr>
          </thead>
          <tbody data-testid="imp-log-body">
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-[#6b7280]">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-[#6b7280]">
                No impersonation events in this window.
              </td></tr>
            ) : events.map((e, i) => (
              <tr key={i} className="border-t border-[#1f2937]" data-testid={`imp-row-${i}`}>
                <td className="px-3 py-1.5 text-[#9ca3af]">{(e.timestamp || "").slice(0, 19)}</td>
                <td className="px-3 py-1.5 text-[#dc2626]">{e.admin_email}</td>
                <td className="px-3 py-1.5 text-[#fbbf24]">{e.target_email || e.target_user_id?.slice(0, 8) + "…"}</td>
                <td className="px-3 py-1.5 text-[#e5e7eb]">{e.endpoint}</td>
                <td className="px-3 py-1.5 text-[#3b82f6]">{e.method}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
